import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  useTheme,
  Tooltip,
  IconButton,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../contexts/AuthContext';
import { REGISTER_DISABLED } from '../config';
import ThemeToggle from './ThemeToggle';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const theme = useTheme();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  return (
    <Box sx={{ 
      bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'white', 
      borderBottom: `1px solid ${theme.palette.mode === 'dark' ? '#333' : '#e0e0e0'}`,
      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
      mb: 4
    }}>
      <Container maxWidth="lg">
        <Box sx={{ 
          py: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {/* Logo and Brand */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              cursor: 'pointer'
            }} 
            onClick={() => navigate('/')}
          >
            <img 
              src="/favicon.ico" 
              alt="Glowspirit" 
              style={{ height: 32, width: 32 }}
            />
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 600, 
                color: theme.palette.mode === 'dark' ? '#fff' : '#1a237e'
              }}
            >
              Glowspirit
            </Typography>
          </Box>

          {/* Navigation Links */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate('/')}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  color: theme.palette.mode === 'dark' ? '#90caf9' : '#1a237e'
                }
              }}
            >
              Home
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate('/showcase')}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  color: theme.palette.mode === 'dark' ? '#90caf9' : '#1a237e'
                }
              }}
            >
              Features
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate('/help')}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  color: theme.palette.mode === 'dark' ? '#90caf9' : '#1a237e'
                }
              }}
            >
              Help
            </Button>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate('/contact')}
              sx={{
                color: 'text.primary',
                '&:hover': {
                  color: theme.palette.mode === 'dark' ? '#90caf9' : '#1a237e'
                }
              }}
            >
              Contact
            </Button>
          </Box>

          {/* Auth Buttons / User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {user ? (
              <>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: 'text.secondary',
                    fontWeight: 500
                  }}
                >
                  {user.businessName || user.email}
                </Typography>
                <Tooltip title="Settings">
                  <IconButton
                    onClick={() => navigate('/settings')}
                    sx={{ 
                      color: theme.palette.mode === 'dark' ? '#fff' : '#1a237e',
                      '&:hover': {
                        color: theme.palette.mode === 'dark' ? '#90caf9' : '#000051',
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : 'rgba(26, 35, 126, 0.04)'
                      }
                    }}
                  >
                    <SettingsIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate(user.role === 'admin' ? '/admin' : '/dashboard')}
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' ? '#2196f3' : '#1a237e',
                    '&:hover': {
                      bgcolor: theme.palette.mode === 'dark' ? '#1976d2' : '#000051'
                    }
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleLogout}
                  sx={{ 
                    borderColor: theme.palette.mode === 'dark' ? '#2196f3' : '#1a237e',
                    color: theme.palette.mode === 'dark' ? '#2196f3' : '#1a237e',
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? '#1976d2' : '#000051',
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.04)' : 'rgba(26, 35, 126, 0.04)'
                    }
                  }}
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="text" 
                  color="inherit" 
                  onClick={() => navigate('/login')}
                  sx={{ 
                    color: 'text.primary',
                    '&:hover': {
                      color: theme.palette.mode === 'dark' ? '#90caf9' : '#1a237e'
                    }
                  }}
                >
                  Sign In
                </Button>
                {REGISTER_DISABLED ? (
                  <Tooltip title="Registration is closed right now">
                    <span>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        disabled
                        sx={{ 
                          bgcolor: theme.palette.mode === 'dark' ? '#2196f3' : '#1a237e',
                          opacity: 0.5,
                          cursor: 'not-allowed'
                        }}
                      >
                        Sign Up
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={() => navigate('/register')}
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' ? '#2196f3' : '#1a237e',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'dark' ? '#1976d2' : '#000051'
                      }
                    }}
                  >
                    Sign Up
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Header; 